import { useState, type DragEvent } from 'react'
import { REPORT_FILE_PROMPT } from '../content/reportFilePrompt'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { downloadTestExport, useReportFileMutations, useReportFileStorage, useReportFiles, useTenants, type ReportFileDto, type WorkbookAnalysisDto } from '../api/admin'
import { useAuth } from '../auth/AuthProvider'
import { Badge, Empty, Modal, fmtDateTime } from '../components/ui'
import { errMsg } from './TemplatesPage'

const ACCEPT = '.xlsx,.xlsm,.docx'
const kb = (bytes?: number) => (bytes == null ? '' : `${Math.max(1, Math.round(bytes / 1024))} KB`)
const pretty = (v: unknown) => JSON.stringify(v ?? {}, null, 2)

/** SuperAdmin: per-tenant report template files (custom Excel/Word workbooks + cell mappings) and where they live on disk. */
export default function ReportFilesPage() {
  const { tenantId = '' } = useParams()
  const { t } = useTranslation()
  const { user } = useAuth()
  const isSuper = user?.roles.some((r) => r.role === 'SuperAdmin') ?? false
  const tenants = useTenants(isSuper)
  const files = useReportFiles(tenantId, isSuper)
  const storage = useReportFileStorage(tenantId, isSuper)
  const m = useReportFileMutations(tenantId)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [mappingFor, setMappingFor] = useState<ReportFileDto | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  if (!isSuper) return <p className="error">{t('tenants.superOnly')}</p>
  const tenant = tenants.data?.items.find((x) => x.id === tenantId)

  const run = async (key: string, fn: () => Promise<unknown>, done?: string) => {
    setError(null); setNotice(null); setBusy(key)
    try { await fn(); if (done) setNotice(done) } catch (e) { setError(errMsg(e)) } finally { setBusy(null) }
  }

  return (
    <div className="stack">
      <div className="row between wrap">
        <div>
          <Link to="/tenants" className="small">← {t('nav.tenants')}</Link>
          <h2 style={{ margin: 0 }}>{t('reportFiles.title')}</h2>
          <div className="muted">{tenant?.name ?? (tenants.isLoading ? t('common.loading') : tenantId)}{tenant ? <span className="small"> · {tenant.slug}</span> : null}</div>
        </div>
      </div>
      <p className="muted small" style={{ margin: 0 }}>{t('reportFiles.hint')}</p>

      <section className="card">
        <h3>{t('reportFiles.storage')}</h3>
        {storage.isLoading ? <p>{t('common.loading')}</p> : !storage.data ? <p className="error small">{t('common.loadFailed')}</p> : (
          <div className="stack" style={{ gap: 8 }}>
            <dl className="dl">
              <div><dt>{t('reportFiles.rootPath')}</dt><dd><code className="mono">{storage.data.rootPath}</code></dd></div>
              <div><dt>{t('reportFiles.tenantFolder')}</dt><dd><code className="mono">{storage.data.tenantFolder}</code></dd></div>
              <div><dt>{t('reportFiles.layout')}</dt><dd><code className="mono">{storage.data.layout}</code></dd></div>
            </dl>
            <div className="table-wrap">
              <table className="table compact" style={{ maxWidth: 640 }}>
                <thead><tr><th>{t('reportFiles.category')}</th><th>{t('reportFiles.categoryDesc')}</th></tr></thead>
                <tbody>{Object.entries(storage.data.categories).map(([k, v]) => <tr key={k}><td><code className="mono">{k}</code></td><td>{v}</td></tr>)}</tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      <UploadCard
        templates={files.data ?? []}
        analyze={(file) => m.analyze.mutateAsync(file)}
        analyzing={m.analyze.isPending}
        upload={(p) => m.upload.mutateAsync(p)}
        uploading={m.upload.isPending}
      />

      <section className="card">
        <h3>{t('reportFiles.templates')}</h3>
        {files.isLoading ? <p>{t('common.loading')}</p> : !files.data?.length ? <Empty text={t('reportFiles.empty')} /> : (
          <div className="table-wrap"><table className="table">
            <thead><tr><th>{t('templates.code')}</th><th>{t('templates.name')}</th><th>{t('templates.kind')}</th><th>{t('templates.version')}</th><th>{t('reportFiles.file')}</th><th>{t('reportFiles.storagePath')}</th><th>{t('reportFiles.uploadedAt')}</th><th /></tr></thead>
            <tbody>{files.data.map((f) => (
              <tr key={f.templateId}>
                <td><Link to={`/templates/${f.templateId}`}><b>{f.templateCode}</b></Link>{f.mappingName ? <div className="muted small">{f.mappingName}</div> : null}</td>
                <td>{f.templateName}</td>
                <td><Badge>{t(`formKind.${f.kind}`, f.kind)}</Badge></td>
                <td className="small">{f.versionNo != null ? <>v{f.versionNo}{f.versionStatus ? <span className="muted"> · {t(`versionStatus.${f.versionStatus}`, f.versionStatus)}</span> : null}</> : <span className="muted">{t('templates.noPublished')}</span>}</td>
                <td className="small">
                  {f.usesPlatformDefault || !f.hasFile ? <Badge color="#9aa3ad">{t('reportFiles.platformDefault')}</Badge> : (
                    <>
                      <div>{f.fileName}{f.fileSizeBytes != null ? <span className="muted"> · {kb(f.fileSizeBytes)}</span> : null}</div>
                      {f.fileKind ? <Badge color={f.fileKind === 'Word' ? '#2a78d6' : '#008300'}>{f.fileKind}</Badge> : null}
                    </>
                  )}
                </td>
                <td>{f.storagePath ? <code className="mono small" style={{ wordBreak: 'break-all' }}>{f.storagePath}</code> : <span className="muted">–</span>}</td>
                <td className="small" style={{ whiteSpace: 'nowrap' }}>{f.uploadedAt ? fmtDateTime(f.uploadedAt) : '–'}</td>
                <td>
                  <div className="row wrap" style={{ gap: 2 }}>
                    <button className="ghost small-btn" onClick={() => setMappingFor(f)}>{t('reportFiles.editMapping')}</button>
                    <button className="ghost small-btn" disabled={busy !== null} onClick={() => void run(`xlsx:${f.templateId}`, () => downloadTestExport(tenantId, f.templateId, 'excel'))}>{busy === `xlsx:${f.templateId}` ? t('common.pleaseWait') : t('reportFiles.testExcel')}</button>
                    <button className="ghost small-btn" disabled={busy !== null} onClick={() => void run(`pdf:${f.templateId}`, () => downloadTestExport(tenantId, f.templateId, 'pdf'))}>{busy === `pdf:${f.templateId}` ? t('common.pleaseWait') : t('reportFiles.testPdf')}</button>
                    {f.hasFile && !f.usesPlatformDefault ? <button className="ghost small-btn" style={{ color: 'var(--danger)' }} disabled={busy !== null} onClick={() => { if (confirm(t('reportFiles.removeConfirm', { name: f.fileName ?? f.templateCode }))) void run(`rm:${f.templateId}`, () => m.removeFile.mutateAsync(f.templateId), t('reportFiles.removed')) }}>{t('reportFiles.removeFile')}</button> : null}
                    <Link className="button ghost small-btn" to={`/templates/${f.templateId}`}>{t('reportFiles.openBuilder')}</Link>
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
        {notice ? <p className="small" style={{ color: '#008300' }}>{notice}</p> : null}
        {error ? <p className="error">{error}</p> : null}
      </section>

      {mappingFor ? (
        <MappingModal
          file={mappingFor}
          saving={m.saveMapping.isPending}
          onClose={() => setMappingFor(null)}
          onSave={(mapping) => m.saveMapping.mutateAsync({ templateId: mappingFor.templateId, mapping }).then(() => { setMappingFor(null); setNotice(t('reportFiles.mappingSaved')) })}
        />
      ) : null}
    </div>
  )
}

function UploadCard({ templates, analyze, analyzing, upload, uploading }: {
  templates: ReportFileDto[]
  analyze: (file: File) => Promise<WorkbookAnalysisDto>
  analyzing: boolean
  upload: (p: { file: File; templateId?: string; definitionJson?: string; mappingJson?: string; name?: string }) => Promise<ReportFileDto>
  uploading: boolean
}) {
  const { t } = useTranslation()
  const [file, setFile] = useState<File | null>(null)
  const [drag, setDrag] = useState(false)
  const [packageJson, setPackageJson] = useState('')
  const [analysis, setAnalysis] = useState<WorkbookAnalysisDto | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const jsonValid = isJsonObject(packageJson)
  const canUpload = !!file && jsonValid && !uploading
  const existingCode = (() => {
    try { const p = JSON.parse(packageJson) as { template?: { code?: string }; code?: string }; return p.template?.code ?? p.code } catch { return undefined }
  })()
  const existing = existingCode ? templates.find((x) => x.templateCode === existingCode) : undefined

  const pick = (f: File | undefined) => {
    if (!f) return
    if (!/\.(xlsx|xlsm|docx)$/i.test(f.name)) { setError(t('reportFiles.badExtension')); return }
    setError(null); setSuccess(null); setAnalysis(null); setFile(f)
  }
  const onDrop = (e: DragEvent) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]) }
  const readJsonFile = (f: File | undefined) => { if (!f) return; void f.text().then((txt) => setPackageJson(txt)).catch(() => setError(t('reportFiles.jsonReadFailed'))) }
  const copyPrompt = () => {
    navigator.clipboard.writeText(REPORT_FILE_PROMPT).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) }).catch(() => setError(t('reportFiles.copyFailed')))
  }
  const doAnalyze = () => {
    if (!file) return
    setError(null); setSuccess(null)
    analyze(file).then(setAnalysis).catch((e: unknown) => setError(errMsg(e)))
  }
  const doUpload = () => {
    if (!file) return
    setError(null); setSuccess(null)
    upload({ file, definitionJson: packageJson })
      .then((r) => { setSuccess(t('reportFiles.uploaded', { code: r.templateCode, file: r.fileName ?? file.name })); setFile(null); setAnalysis(null); setPackageJson('') })
      .catch((e: unknown) => setError(errMsg(e)))
  }

  return (
    <section className="card stack">
      <h3 style={{ margin: 0 }}>{t('reportFiles.uploadTitle')}</h3>
      <ol className="small muted" style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7 }}>
        <li>{t('reportFiles.step1')}</li>
        <li>{t('reportFiles.step2')}</li>
        <li>{t('reportFiles.step3')}</li>
      </ol>
      <details>
        <summary><b>{t('reportFiles.promptTitle')}</b> <span className="muted small">· {t('reportFiles.promptHint')}</span></summary>
        <div className="stack" style={{ marginTop: 8 }}>
          <span><button type="button" className="secondary small-btn" onClick={copyPrompt}>{copied ? t('reportFiles.copied') : t('reportFiles.copyPrompt')}</button></span>
          <textarea readOnly rows={14} value={REPORT_FILE_PROMPT} onFocus={(e) => e.currentTarget.select()} style={{ fontFamily: 'monospace', fontSize: '0.75rem' }} />
        </div>
      </details>
      <div className={`dropzone big ${drag ? 'drag' : ''}`} onDragOver={(e) => { e.preventDefault(); setDrag(true) }} onDragLeave={() => setDrag(false)} onDrop={onDrop}>
        {file ? <div><b>{file.name}</b> <span className="muted small">· {kb(file.size)}</span></div> : <span className="muted">{t('reportFiles.dropHint')}</span>}
        <label className="button secondary small-btn">{t('reportFiles.pickFile')}<input type="file" accept={ACCEPT} hidden onChange={(e) => { pick(e.target.files?.[0]); e.target.value = '' }} /></label>
      </div>
      <label>{t('reportFiles.packageJson')}
        <textarea rows={10} value={packageJson} onChange={(e) => setPackageJson(e.target.value)} placeholder='{ "template": { "code": "...", "name": "...", "kind": "Checklist", ... }, "mapping": { "cells": [], "tables": [] } }' spellCheck={false} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} />
        <span className="row wrap" style={{ gap: 8, alignItems: 'center' }}>
          <label className="button secondary small-btn" style={{ display: 'inline-flex' }}>{t('reportFiles.pickJson')}<input type="file" accept=".json,application/json" hidden onChange={(e) => { readJsonFile(e.target.files?.[0]); e.target.value = '' }} /></label>
          {packageJson.trim() && !jsonValid ? <span className="error small">{t('reportFiles.invalidJson')}</span> : null}
          {existing ? <span className="small" style={{ color: '#eda100' }}>{t('reportFiles.willReplace', { code: existing.templateCode })}</span> : existingCode ? <span className="muted small">{t('reportFiles.willCreate', { code: existingCode })}</span> : null}
        </span>
      </label>
      <div className="row wrap">
        <button disabled={!canUpload} onClick={doUpload}>{uploading ? t('common.pleaseWait') : t('reportFiles.upload')}</button>
        <button className="secondary" disabled={!file || analyzing} onClick={doAnalyze}>{analyzing ? t('common.pleaseWait') : t('reportFiles.analyze')}</button>
        {file && !packageJson.trim() ? <span className="muted small">{t('reportFiles.jsonRequired')}</span> : null}
      </div>
      {success ? <p className="small" style={{ color: '#008300', margin: 0 }}>{success}</p> : null}
      {error ? <p className="error" style={{ margin: 0 }}>{error}</p> : null}
      {analysis ? <AnalysisView a={analysis} onUseMapping={() => undefined} onUseDefinition={(json) => setPackageJson(JSON.stringify({ template: JSON.parse(json) as unknown, mapping: analysis.suggestedMapping ?? {} }, null, 2))} /> : null}
    </section>
  )
}

function AnalysisView({ a, onUseMapping, onUseDefinition }: { a: WorkbookAnalysisDto; onUseMapping: (json: string) => void; onUseDefinition: (json: string) => void }) {
  const { t } = useTranslation()
  const suggested = a.suggestedMapping ? pretty(a.suggestedMapping) : ''
  const suggestedDefinition = a.suggestedDefinition ? pretty(a.suggestedDefinition) : ''
  return (
    <div className="card stack" style={{ background: 'var(--hover)' }}>
      <div className="row wrap">
        <b>{t('reportFiles.analysis')}</b>
        <Badge color={a.fileKind === 'Word' ? '#2a78d6' : '#008300'}>{a.fileKind}</Badge>
        <span className="muted small">{a.fileName}</span>
        {a.detectedTemplateCode ? <span className="small">{t('reportFiles.detected')}: <b>{a.detectedTemplateCode}</b></span> : null}
        <span className="small">{t('reportFiles.detectedQuestions', { count: a.detectedQuestionCount ?? 0 })}</span>
      </div>
      {a.fileKind === 'Word' || a.placeholders.length > 0 ? (
        <div>
          <div className="small"><b>{t('reportFiles.placeholders')}</b> ({a.placeholders.length})</div>
          {a.placeholders.length === 0 ? <span className="muted small">{t('common.none')}</span> : <div className="row wrap" style={{ gap: 4 }}>{a.placeholders.map((p) => <code key={p} className="mono small">{p}</code>)}</div>}
        </div>
      ) : null}
      {a.sheets.map((s) => (
        <details key={s.name}>
          <summary className="small"><b>{s.name}</b> <span className="muted">· {t('reportFiles.sheetSize', { rows: s.lastRow, cols: s.lastColumn, cells: s.cells.length })}</span></summary>
          <div className="table-wrap" style={{ maxHeight: 320, overflowY: 'auto' }}>
            <table className="table compact">
              <thead><tr><th>{t('reportFiles.cell')}</th><th>{t('reportFiles.text')}</th></tr></thead>
              <tbody>{s.cells.map((c) => <tr key={c.address}><td><code className="mono">{c.address}</code></td><td className="small" style={{ whiteSpace: 'pre-wrap' }}>{c.text}</td></tr>)}</tbody>
            </table>
          </div>
        </details>
      ))}
      {suggestedDefinition ? (
        <label>{t('reportFiles.suggestedDefinition')}
          <textarea rows={8} readOnly value={suggestedDefinition} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} />
          <span><button type="button" className="secondary small-btn" onClick={() => onUseDefinition(suggestedDefinition)}>{t('reportFiles.copyToDefinition')}</button></span>
        </label>
      ) : null}
      {suggested ? (
        <label>{t('reportFiles.suggestedMapping')}
          <textarea rows={8} readOnly value={suggested} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} />
          <span><button type="button" className="secondary small-btn" onClick={() => onUseMapping(suggested)}>{t('reportFiles.copyToMapping')}</button></span>
        </label>
      ) : null}
    </div>
  )
}

function MappingModal({ file, saving, onClose, onSave }: { file: ReportFileDto; saving: boolean; onClose: () => void; onSave: (mapping: Record<string, unknown>) => Promise<unknown> }) {
  const { t } = useTranslation()
  const [json, setJson] = useState(() => pretty(file.mapping))
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null)
  const validate = (): Record<string, unknown> | null => {
    try {
      const parsed: unknown = JSON.parse(json)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) { setStatus({ ok: false, text: t('reportFiles.mappingNotObject') }); return null }
      setStatus({ ok: true, text: t('reportFiles.jsonValid') })
      return parsed as Record<string, unknown>
    } catch (e) {
      setStatus({ ok: false, text: `${t('reportFiles.invalidJson')}: ${e instanceof Error ? e.message : String(e)}` })
      return null
    }
  }
  return (
    <Modal title={`${t('reportFiles.editMapping')} · ${file.templateCode}`} onClose={onClose} width={800}>
      <p className="muted small" style={{ margin: 0 }}>{t('reportFiles.mappingModalHint')}</p>
      <textarea rows={22} value={json} onChange={(e) => { setJson(e.target.value); setStatus(null) }} spellCheck={false} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} />
      {status ? <p className={`small ${status.ok ? '' : 'error'}`} style={{ margin: 0, color: status.ok ? '#008300' : undefined }}>{status.text}</p> : null}
      <div className="row between wrap">
        <button className="secondary" onClick={() => { const v = validate(); if (v) setJson(pretty(v)) }}>{t('reportFiles.validate')}</button>
        <div className="row">
          <button className="secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button disabled={saving} onClick={() => { const v = validate(); if (v) onSave(v).catch((e: unknown) => setStatus({ ok: false, text: errMsg(e) })) }}>{saving ? t('common.pleaseWait') : t('common.save')}</button>
        </div>
      </div>
    </Modal>
  )
}

function isJsonObject(s: string): boolean {
  try { const v: unknown = JSON.parse(s); return !!v && typeof v === 'object' && !Array.isArray(v) } catch { return false }
}
